import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getBriefPdfModules, getBriefPdfSections } from 'features/briefs/briefPdfUtility';
import BriefModule from 'features/briefs/components/atoms/BriefModule';
import BriefProductLinks from 'features/briefs/components/atoms/BriefProductLinks';
import CheckboxDisplay from 'features/briefs/components/atoms/CheckboxDisplay';
import { Box, Text } from 'shared/components/display';
import { DefinitionList, ListDetail } from 'shared/components/molecules/DefinitionList';
import Richtext from 'shared/components/Richtext';
import { isRichtextEmpty } from 'shared/components/Richtext/shared/utils';

import { BRIEF_COPY } from 'shared/config/copy';
import COLORS from 'shared/styles/colors';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { Brief } from 'shared/typings/briefs';

import styles from './styles';

export const BRIEF_PDF_ROOT_ID = 'brief-pdf-root';

interface Props {
  brief: Brief;
}

/**
 * Print-only rendering of a brief. Unlike the on-screen brief, every section and every
 * module is laid out in a single flow so nothing is hidden behind a tab or cropped when
 * the browser paginates it. Pricing is intentionally absent — see BRIEF_PDF_EXCLUDED_SECTIONS.
 */
const BriefPdfDocument: React.FC<Props> = ({ brief }) => {
  const modules = getBriefPdfModules(brief);

  // Richtext fields are stored as JSON strings and can be absent on partially loaded briefs.
  const parseRichtext = (value?: string) => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      return isRichtextEmpty(parsed) ? null : parsed;
    } catch {
      return null;
    }
  };

  const description = parseRichtext(brief.description);
  const creatorRequirements = parseRichtext(brief.creatorRequirements);
  const contentGuidelines = parseRichtext(brief.contentGuidelines);
  const dosAndDonts = parseRichtext(brief.dosAndDonts);
  const valuePropositions = parseRichtext(brief.valuePropositions);

  const renderSection = (id: string) => {
    switch (id) {
      case 'Overview':
        return (
          <DefinitionList labelColor={COLORS.NEUTRAL800}>
            <ListDetail text="Brief Name" labelClassName="brief-pdf__heading">
              {brief.name || BRIEF_COPY.DEFAULT_BRIEF_NAME}
            </ListDetail>
            <ListDetail text="Brief Overview" labelClassName="brief-pdf__heading" hide={!description}>
              <Richtext value={description} key={`${brief.id}-pdf-description`} readOnly />
            </ListDetail>
            <ListDetail
              text="Product / Service"
              labelClassName="brief-pdf__heading"
              hide={!brief.website?.length && !brief.shippingRequired}
            >
              {brief.website?.length ? <BriefProductLinks website={brief.website} /> : <></>}
              {brief.shippingRequired ? <CheckboxDisplay text="Shipping Required" /> : <></>}
            </ListDetail>
          </DefinitionList>
        );
      case 'Creator Requirements':
        return (
          <DefinitionList labelColor={COLORS.NEUTRAL800}>
            <ListDetail
              text="Creator Requirements"
              labelClassName="brief-pdf__heading"
              hide={!creatorRequirements}
            >
              <Richtext value={creatorRequirements} key={`${brief.id}-pdf-creator-requirements`} readOnly />
            </ListDetail>
            <ListDetail text="Creator Confirmation" labelClassName="brief-pdf__heading" hide={!brief.requireConfirmation}>
              <CheckboxDisplay text="Require Creator Confirmation" />
            </ListDetail>
            <ListDetail
              text="In-Shop Visit Required"
              labelClassName="brief-pdf__heading"
              hide={!brief.states?.length}
            >
              {brief.states?.map((state) => state.code).join(', ') || ''}
            </ListDetail>
            <ListDetail
              text="Auto-Response to Approved Creators"
              labelClassName="brief-pdf__heading"
              hide={!brief.autoResponse}
            >
              {brief.autoResponse}
            </ListDetail>
            <ListDetail text="Invite to Apply Only" labelClassName="brief-pdf__heading" hide={!brief.inviteOnly}>
              <CheckboxDisplay text="Invite to Apply Only" />
            </ListDetail>
          </DefinitionList>
        );
      case 'Content Guidelines':
        return (
          <DefinitionList labelColor={COLORS.NEUTRAL800}>
            <ListDetail text="Content Guidelines" labelClassName="brief-pdf__heading" hide={!contentGuidelines}>
              <Richtext value={contentGuidelines} key={`${brief.id}-pdf-content-guidelines`} readOnly />
            </ListDetail>
            <ListDetail text="Dos & Don’ts" labelClassName="brief-pdf__heading" hide={!dosAndDonts}>
              <Richtext value={dosAndDonts} key={`${brief.id}-pdf-dos-donts`} readOnly />
            </ListDetail>
            <ListDetail text="Value Propositions" labelClassName="brief-pdf__heading" hide={!valuePropositions}>
              <Richtext value={valuePropositions} key={`${brief.id}-pdf-value-propositions`} readOnly />
            </ListDetail>
          </DefinitionList>
        );
      case 'Module Requirements':
        return (
          <>
            {modules.map((module) => (
              <Box className="brief-pdf__module" key={`${module.id}-pdf-module`}>
                <Text variant={TEXT_VARIANTS.H5} className="brief-pdf__module-title">
                  {`Module ${module.moduleNum}: ${module.name}`}
                </Text>
                <BriefModule module={module} />
              </Box>
            ))}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box css={styles} id={BRIEF_PDF_ROOT_ID} className="brief-pdf" dataCy="brief-pdf">
      <Box className="brief-pdf__title-block">
        <Text variant={TEXT_VARIANTS.H3} className="brief-pdf__title">
          {brief.name || BRIEF_COPY.DEFAULT_BRIEF_NAME}
        </Text>
        {brief.account?.name && <Text className="brief-pdf__subtitle">{brief.account.name}</Text>}
      </Box>
      {getBriefPdfSections().map(({ id, title, icon }) => (
        <Box className="brief-pdf__section" key={`${id}-pdf-section`}>
          <Text variant={TEXT_VARIANTS.H4} className="brief-pdf__section-title">
            <FontAwesomeIcon icon={icon} className="brief-pdf__section-icon" />
            {title}
          </Text>
          {renderSection(id)}
        </Box>
      ))}
    </Box>
  );
};

export default BriefPdfDocument;
